import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['per-diem-vs-empower'];

export default function PerDiemVsEmpowerPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['empower-alternatives-fire', 'monarch-money-alternatives-fire']}
    >
      <QuickVerdict
        title="Quick verdict"
        items={[
          'Choose Empower if you want a familiar, mostly free net worth dashboard and retirement planner.',
          'Choose Per Diem if you want budgeting, current spending, and FIRE planning to influence each other in one workflow.',
          'Empower is a better fit for broad visibility. Per Diem is a better fit for connected decision-making.',
        ]}
      />

      <ArticleSection title="Per Diem and Empower solve different versions of the same problem">
        <ArticleParagraph>
          Both products are trying to answer a similar high-level question: how healthy
          is your financial life, and where is it going? The difference is in what happens
          after you open the app.
        </ArticleParagraph>
        <ArticleParagraph>
          Empower is strongest as a broad financial dashboard. It gives you a consolidated
          view of accounts, net worth, and retirement. That is useful, especially if your
          biggest problem is fragmentation at the visibility layer.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is built around a narrower but more operational question: what can I
          spend today, and what does that mean for my long-term plan? The product is more
          opinionated because it is trying to connect day-to-day behavior to financial
          independence planning, not just summarize the balance sheet.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileAccounts}
        alt="Per Diem mobile accounts screen"
        caption="Per Diem still covers account and balance visibility, but the product is designed to push that visibility into actual day-to-day decisions."
        variant="phone"
      />

      <ArticleSection title="Where Empower still has the edge">
        <ArticleBullets
          items={[
            'It is widely known and easy to explain to someone who wants a familiar net worth dashboard.',
            'The free entry point makes it easy to try if cost sensitivity is high.',
            'It is a natural fit for households that mainly want aggregation and high-level retirement visibility.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Where Per Diem is the stronger choice">
        <ArticleParagraph>
          Per Diem is the better fit if your problem is not just “I want to see my finances,”
          but “I want my budgeting and retirement planning to stop living in separate systems.”
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Per Diem puts a daily spending number at the center of the product instead of burying decision-making behind account totals.',
            'It combines transactions, budgeting, and FIRE planning so the model can react to actual household behavior.',
            'It is built for people who do not want to keep reconciling a budgeting app with a separate retirement tool.',
          ]}
        />
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileHome}
        alt="Per Diem mobile home screen with per diem metric"
        caption="The product difference shows up fast: Per Diem leads with a daily spending decision, not just a dashboard summary."
        variant="phone"
      />

      <ArticleSection title="Budgeting is the clearest difference">
        <ArticleParagraph>
          For many households, the practical reason to switch away from Empower is not the
          balance sheet. It is the lack of a tighter budgeting workflow.
        </ArticleParagraph>
        <ArticleParagraph>
          FIRE planning is not only about the assets you already have. It is also about
          the spending behavior you repeat every month. If your current tool is strong on
          net worth but weak on budgeting, you end up solving the most important part elsewhere.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Retirement planning matters, but so does how often it updates">
        <ArticleParagraph>
          A retirement plan is more useful when it does not feel detached from your current
          life. One of the common frustrations in this category is that the user can see a
          long-term plan in one tab and a very different day-to-day reality in another.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is trying to shrink that gap. The reason the daily spending frame matters
          is that it translates a long-term model into something the household can actually act on.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileFirePlan}
        alt="Per Diem mobile FIRE plan screen"
        caption="For FIRE-minded households, the useful question is not just “How am I doing?” but “What does my current behavior do to my FI timeline?”"
        variant="phone"
      />

      <ArticleSection title="What type of household should choose Empower">
        <ArticleBullets
          items={[
            'Households that want a recognizable, largely free dashboard and do not need budgeting at the center.',
            'People who mostly want account aggregation, net worth tracking, and periodic retirement visibility.',
            'Users who are comfortable supplementing the product with another budgeting or planning workflow if needed.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="What type of household should choose Per Diem">
        <ArticleBullets
          items={[
            'Households pursuing FIRE or financial optionality who want current spending tied to long-term planning.',
            'People who already feel the pain of juggling a budgeting app, retirement tool, and spreadsheet.',
            'Users who want a more explicit answer to “Can I spend this and still stay on track?”',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="The simplest way to decide">
        <ArticleParagraph>
          If your main need is visibility, Empower is still a reasonable choice.
          If your main need is decision support, especially around spending and FIRE,
          Per Diem is the more interesting option.
        </ArticleParagraph>
        <ArticleParagraph>
          That is the clearest distinction between the two products. One is mainly a dashboard.
          The other is trying to become a daily planning system.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
